import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function SASS_Tutorial() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎨 SASS Tutorial</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        <b>SASS (Syntactically Awesome Stylesheets)</b> is a CSS preprocessor
        that extends CSS with powerful features like variables, nesting, mixins,
        functions, and more. SASS also comes with many <b>built-in functions</b>{" "}
        for working with strings, numbers, lists, maps, selectors, colors, and
        introspection.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Features</h2>
        <ul className="tutorial-list-points">
          <li>✅ Variables, Nesting, Mixins, Extends, and Functions.</li>
          <li>
            ✅ Sass <b>String Functions</b> (e.g., <code>to-upper-case()</code>,{" "}
            <code>quote()</code>).
          </li>
          <li>
            ✅ Sass <b>Numeric Functions</b> (e.g., <code>min()</code>,{" "}
            <code>max()</code>, <code>round()</code>).
          </li>
          <li>
            ✅ Sass <b>List Functions</b> (e.g., <code>length()</code>,{" "}
            <code>nth()</code>, <code>join()</code>).
          </li>
          <li>
            ✅ Sass <b>Map Functions</b> (e.g., <code>map-get()</code>,{" "}
            <code>map-merge()</code>).
          </li>
          <li>
            ✅ Sass <b>Selector Functions</b> (e.g.,{" "}
            <code>is-superselector()</code>).
          </li>
          <li>
            ✅ Sass <b>Introspection Functions</b> (e.g., <code>type-of()</code>
            , <code>unit()</code>).
          </li>
          <li>
            ✅ Sass <b>Color Functions</b> (e.g., <code>lighten()</code>,{" "}
            <code>darken()</code>, <code>rgba()</code>).
          </li>
        </ul>
      </div>

      {/* STRING FUNCTIONS */}
      <div className="tutorial-section">
        <h2 className="section-title">📝 Sass String Functions</h2>
        <p>
          Sass provides string manipulation functions like{" "}
          <code>to-upper-case()</code>, <code>to-lower-case()</code>,{" "}
          <code>quote()</code>, and <code>unquote()</code>.
        </p>
        <CodeCopyBlock
          code={`$name: "hello world";

.upper {
  content: to-upper-case($name); // "HELLO WORLD"
}

.lower {
  content: to-lower-case($name); // "hello world"
}

.quoted {
  content: quote(hello); // "hello"
}

.unquoted {
  content: unquote("hello"); // hello
}`}
        />
      </div>

      {/* NUMERIC FUNCTIONS */}
      <div className="tutorial-section">
        <h2 className="section-title">🔢 Sass Numeric Functions</h2>
        <p>
          Sass allows mathematical operations and provides functions like{" "}
          <code>abs()</code>, <code>round()</code>, <code>ceil()</code>,{" "}
          <code>floor()</code>, <code>percentage()</code>.
        </p>
        <CodeCopyBlock
          code={`.numbers {
  width: abs(-10px);      // 10px
  height: round(12.6px);  // 13px
  margin: percentage(0.5); // 50%
}`}
        />
      </div>

      {/* LIST FUNCTIONS */}
      <div className="tutorial-section">
        <h2 className="section-title">📋 Sass List Functions</h2>
        <p>
          Lists in Sass are like arrays. You can use <code>length()</code>,{" "}
          <code>nth()</code>, <code>join()</code>, and <code>append()</code>.
        </p>
        <CodeCopyBlock
          code={`$list: 10px, 20px, 30px;

.list {
  len: length($list);        // 3
  second: nth($list, 2);     // 20px
  joined: join($list, 40px); // 10px, 20px, 30px, 40px
}`}
        />
      </div>

      {/* MAP FUNCTIONS */}
      <div className="tutorial-section">
        <h2 className="section-title">🗺️ Sass Map Functions</h2>
        <p>
          Maps are like objects/dictionaries. Use <code>map-get()</code>,{" "}
          <code>map-merge()</code>, and <code>map-remove()</code>.
        </p>
        <CodeCopyBlock
          code={`$colors: (
  primary: blue,
  secondary: green
);

.map {
  primary-color: map-get($colors, primary); // blue
  merged: map-merge($colors, (danger: red));
}`}
        />
      </div>

      {/* SELECTOR FUNCTIONS */}
      <div className="tutorial-section">
        <h2 className="section-title">🔖 Sass Selector Functions</h2>
        <p>
          Sass provides functions for working with selectors like{" "}
          <code>is-superselector()</code> and <code>selector-nest()</code>.
        </p>
        <CodeCopyBlock
          code={`.selectors {
  check: is-superselector("div", "div p"); // true
  nested: selector-nest("ul", "li a");     // ul li a
}`}
        />
      </div>

      {/* INTROSPECTION FUNCTIONS */}
      <div className="tutorial-section">
        <h2 className="section-title">🔍 Sass Introspection Functions</h2>
        <p>
          Introspection functions let you check the type and properties of
          values, like <code>type-of()</code>, <code>unit()</code>,{" "}
          <code>unitless()</code>.
        </p>
        <CodeCopyBlock
          code={`.check {
  type: type-of(15px);    // number
  unit: unit(100px);      // px
  no-unit: unitless(5);   // true
}`}
        />
      </div>

      {/* COLOR FUNCTIONS */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Sass Color Functions</h2>
        <p>
          Sass has advanced color functions like <code>lighten()</code>,{" "}
          <code>darken()</code>, <code>rgba()</code>, and <code>mix()</code>.
        </p>
        <CodeCopyBlock
          code={`.colors {
  lighter: lighten(#3498db, 20%); // light blue
  darker: darken(#3498db, 20%);   // dark blue
  transparent: rgba(#3498db, 0.5);
  mixed: mix(red, blue, 50%);     // purple
}`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with lists, maps, and selector functions in real UI.
          </li>
          <li>Use color functions to build dynamic themes.</li>
          <li>Try loops + functions for generating utility classes.</li>
          <li>Organize SASS code using partials and imports.</li>
          <li>Integrate SASS functions in a React or Vue project.</li>
        </ul>
      </div>
    </div>
  );
}
